<template>
  <div class="factory-summary">
    <!-- main div -->
    <div class="px-4 pb-6">
      <!-- 死活監視詳細表示部分 -->
      <div class="status-summary mb-4">
        <h2 class="text-lg text-gray-100 font-semibold mb-3">
          端末ステータス一覧
        </h2>

        <!-- ターゲット検索フィールドを追加 -->
        <div class="search-field mb-4">
          <label for="target-search" class="text-gray-100 block mb-1"
            >端末検索:</label
          >
          <input
            id="target-search"
            type="text"
            v-model="targetSearch"
            placeholder="端末名で検索（ALLですべて表示）"
            class="target-input"
            @input="updateTargetSearch"
          />
        </div>

        <!-- 検索結果に基づくステータスカウントパネル -->
        <div class="summary-card mb-4">
          <h3 class="text-md text-gray-100 font-semibold mb-2">
            検索結果ステータス
          </h3>
          <div class="flex justify-between">
            <div class="status-count status-green">
              <div class="status-label">正常</div>
              <div class="status-number">
                {{ filteredStatusCounts.green }}
              </div>
            </div>
            <div class="status-count status-orange">
              <div class="status-label">注意</div>
              <div class="status-number">
                {{ filteredStatusCounts.orange }}
              </div>
            </div>
            <div class="status-count status-red">
              <div class="status-label">異常</div>
              <div class="status-number">{{ filteredStatusCounts.red }}</div>
            </div>
          </div>
        </div>

        <div class="status-grid">
          <div
            v-for="device in filteredStatusData"
            :key="device.device_name"
            class="status-tile"
            :class="getStatusClass(device.status)"
            @click="showDeviceDetail(device)"
          >
            <div class="status-tile-inner">
              <div class="device-name-display">{{ device.device_name }}</div>
              <div class="version-info">
                <span class="version-text">{{ getVersionInfo(device) }}</span>
              </div>
              <!-- 回数表示 -->
              <div class="execution-stats">
                <span class="execution-count"
                  >{{ getDeviceTotalCount(device) }}回</span
                >
              </div>
              <!-- 積み上げバーチャート -->
              <div class="stacked-bar-container">
                <div
                  v-for="(count, target) in device.targets"
                  :key="target"
                  class="stacked-bar-segment"
                  :style="{
                    width: getTargetBarWidth(count, device),
                    backgroundColor: getTargetColor(target),
                  }"
                  :title="`${target}: ${count}回`"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 選択された端末の詳細情報 (固定表示) -->
      <div v-if="selectedDevice" class="device-detail-fixed">
        <div class="card-header">
          <h3 class="device-name">{{ selectedDevice.device_name }}</h3>
          <div
            class="status-badge"
            :class="getStatusClass(selectedDevice.status)"
          >
            {{ getStatusText(selectedDevice.status) }}
          </div>
          <button class="close-button" @click="selectedDevice = null">✕</button>
        </div>
        <div class="card-body">
          <p class="last-execution">
            最終実行: {{ formatDateTime(selectedDevice.last_execution) }}
          </p>
          <div class="version-details">
            <p>
              <span class="detail-label">Sociama: </span
              >{{ selectedDevice.sociama_app_version || "unknown" }}
            </p>
            <p>
              <span class="detail-label">Automation: </span
              >{{ selectedDevice.automation_app_version || "unknown" }}
            </p>
            <p v-if="selectedDevice.memo">
              <span class="detail-label">メモ: </span>{{ selectedDevice.memo }}
            </p>
          </div>
          <div class="targets">
            <h4>ターゲット別データ数</h4>
            <div
              v-for="(count, target) in selectedDevice.targets"
              :key="target"
              class="target-item"
            >
              <span class="target-name">{{ target }}:</span>
              <span class="target-count">{{ count }}</span>
            </div>
            <div
              v-if="Object.keys(selectedDevice.targets).length === 0"
              class="no-data"
            >
              データがありません
            </div>
          </div>
        </div>
      </div>

      <div v-if="filteredStatusData.length === 0" class="no-data-message">
        検索条件に一致する端末がないか、データが読み込まれていません
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import api from "../api/api.js";

export default {
  name: "FactorySummaryHS",
  data() {
    return {
      statusData: [],
      selectedDevice: null,
      statusCounts: {
        green: 0,
        orange: 0,
        red: 0,
      },
      refreshInterval: null,
      isLoading: true,
      targetSearch: "ALL", // ターゲット検索用の入力フィールド

      // 端末ごとのバーチャート関連
      deviceColors: {}, // ターゲットごとの色を保存するオブジェクト
      colorPalette: [
        "#008FFB",
        "#00E396",
        "#FEB019",
        "#FF4560",
        "#775DD0",
        "#3F51B5",
        "#546E7A",
        "#D4526E",
        "#8D5B4C",
        "#F86624",
      ],
    };
  },
  computed: {
    ...mapGetters(["factoryDeviceTraget"]),

    filteredStatusData() {
      if (!this.statusData) return [];

      if (this.targetSearch === "ALL") {
        return this.statusData;
      } else {
        return this.statusData.filter((device) =>
          device.device_name
            .toLowerCase()
            .includes(this.targetSearch.toLowerCase())
        );
      }
    },

    // 検索結果に基づくステータスカウントを計算
    filteredStatusCounts() {
      const counts = {
        green: 0,
        orange: 0,
        red: 0,
      };

      if (this.filteredStatusData) {
        this.filteredStatusData.forEach((device) => {
          if (device.status in counts) {
            counts[device.status]++;
          }
        });
      }

      return counts;
    },
  },
  mounted() {
    // Vuexから検索ターゲットを取得
    this.targetSearch = this.factoryDeviceTraget;

    this.fetchStatusData();

    // 5分ごとに自動更新
    this.refreshInterval = setInterval(() => {
      this.fetchStatusData();
    }, 5 * 60 * 1000);
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    ...mapMutations(["updateFactoryDeviceTraget"]),

    async fetchStatusData() {
      this.isLoading = true;
      try {
        const response = await api.getAutoResultsHSStatus();
        this.statusData = response.data;

        // ステータスカウントをリセット
        this.statusCounts = {
          green: 0,
          orange: 0,
          red: 0,
        };

        // ステータスごとのカウントを計算
        this.statusData.forEach((device) => {
          this.statusCounts[device.status]++;
        });

        // ターゲットに色を割り当て
        this.assignTargetColors();
      } catch (error) {
        console.error("ステータスデータの取得に失敗しました:", error);
        this.$toast.show("データの取得中にエラーが発生しました。", {
          type: "error",
          position: "top-right",
          duration: 5000,
        });
      } finally {
        this.isLoading = false;
      }
    },
    getStatusClass(status) {
      switch (status) {
        case "green":
          return "status-green";
        case "orange":
          return "status-orange";
        case "red":
          return "status-red";
        default:
          return "";
      }
    },
    getStatusText(status) {
      switch (status) {
        case "green":
          return "正常";
        case "orange":
          return "注意";
        case "red":
          return "異常";
        default:
          return "不明";
      }
    },
    formatDateTime(dateTime) {
      if (!dateTime) return "データなし";
      const date = new Date(dateTime);
      return date.toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    getVersionInfo(device) {
      // アプリのバージョン情報を取得
      const sociama = device.sociama_app_version || "unknown";
      const automation = device.automation_app_version || "unknown";
      return `${sociama}/${automation}`;
    },
    updateTargetSearch() {
      // Vuexに検索ターゲットを保存
      this.updateFactoryDeviceTraget(this.targetSearch);
    },
    showDeviceDetail(device) {
      this.selectedDevice = device;
    },

    // 端末ごとの回数表示用メソッド
    getDeviceTotalCount(device) {
      if (!device.targets) return 0;
      return Object.values(device.targets).reduce(
        (sum, count) => sum + count,
        0
      );
    },

    getTargetBarWidth(count, device) {
      const deviceTotal = this.getDeviceTotalCount(device);
      if (deviceTotal === 0) return "0%";
      return `${(count / deviceTotal) * 100}%`;
    },

    getTargetColor(target) {
      // ターゲットに一貫した色を割り当てる
      if (!this.deviceColors[target]) {
        const colorIndex =
          Object.keys(this.deviceColors).length % this.colorPalette.length;
        this.deviceColors[target] = this.colorPalette[colorIndex];
      }
      return this.deviceColors[target];
    },

    // ターゲットの色を割り当てるメソッド
    assignTargetColors() {
      if (!this.statusData) return;

      // すべてのターゲットを収集
      const allTargets = new Set();
      this.statusData.forEach((device) => {
        if (device.targets) {
          Object.keys(device.targets).forEach((target) => {
            allTargets.add(target);
          });
        }
      });

      // 各ターゲットに色を割り当て
      [...allTargets].forEach((target, index) => {
        this.deviceColors[target] =
          this.colorPalette[index % this.colorPalette.length];
      });
    },
  },
};
</script>

<style scoped>
.factory-summary {
  min-height: 100vh;
  background-color: #1a202c;
}

.summary-card {
  background-color: #2d3748;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.status-count {
  flex: 1;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  margin: 0 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.status-count:first-child {
  margin-left: 0;
}

.status-count:last-child {
  margin-right: 0;
}

.status-green {
  background-color: rgba(72, 187, 120, 0.2);
  border: 1px solid #48bb78;
}

.status-orange {
  background-color: rgba(237, 137, 54, 0.2);
  border: 1px solid #ed8936;
}

.status-red {
  background-color: rgba(229, 62, 62, 0.2);
  border: 1px solid #e53e3e;
}

.status-label {
  font-size: 14px;
  color: #e2e8f0;
  margin-bottom: 8px;
}

.status-number {
  font-size: 24px;
  font-weight: bold;
  color: #f7fafc;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

@media (min-width: 640px) {
  .status-grid {
    grid-template-columns: repeat(10, 1fr);
  }
}

@media (min-width: 1024px) {
  .status-grid {
    grid-template-columns: repeat(20, 1fr);
  }
}

.status-tile {
  aspect-ratio: 1/1;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
}

.status-tile:hover {
  transform: scale(1.05);
}

.status-tile-inner {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4px;
  position: relative;
}

.device-name-display {
  position: relative;
  z-index: 1;
}

.version-info {
  position: relative;
  z-index: 2;
}

.execution-stats {
  position: relative;
  z-index: 3;
}

.device-name-display {
  font-weight: bold;
  color: white;
  font-size: 10px;
  text-align: center;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: auto;
  text-overflow: ellipsis;
  width: 100%;
}

.version-info {
  text-align: center;
  margin-bottom: 2px;
}

.version-text {
  font-size: 8px;
  color: white;
  opacity: 0.9;
  display: none; /* 要素を非表示にする */
}

.execution-stats {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2px;
}

.execution-count {
  font-size: 8px;
  font-weight: bold;
  color: white;
}

/* 積み上げバーチャート用スタイル */
.stacked-bar-container {
  width: 100%;
  height: 6px;
  display: flex;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 1px;
}

.stacked-bar-segment {
  height: 100%;
  min-width: 2px;
  transition: width 0.3s ease;
}

.status-tile.status-green {
  background-color: rgba(72, 187, 120, 0.7);
  border: 1px solid #48bb78;
}

.status-tile.status-orange {
  background-color: rgba(237, 137, 54, 0.7);
  border: 1px solid #ed8936;
}

.status-tile.status-red {
  background-color: rgba(229, 62, 62, 0.7);
  border: 1px solid #e53e3e;
}

/* 固定表示の詳細情報 */
.device-detail-fixed {
  position: fixed;
  bottom: 20px; /* ナビゲーションバーがないので調整 */
  left: 0;
  right: 0;
  max-height: 50vh; /* 画面の半分まで */
  overflow-y: auto;
  background-color: #2d3748;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
  z-index: 5;
  margin: 0 10px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: rgba(0, 0, 0, 0.1);
  position: relative;
}

.device-name {
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0;
  flex: 1;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  margin-right: 30px; /* 閉じるボタン用のスペース */
}

.close-button {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.2);
  color: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  font-size: 12px;
}

.close-button:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.status-badge.status-green {
  background-color: #48bb78;
}

.status-badge.status-orange {
  background-color: #ed8936;
}

.status-badge.status-red {
  background-color: #e53e3e;
}

.card-body {
  padding: 16px;
}

.last-execution {
  font-size: 14px;
  color: #cbd5e0;
  margin-top: 0;
  margin-bottom: 12px;
}

.version-details {
  margin-bottom: 12px;
  font-size: 14px;
  color: #e2e8f0;
}

.detail-label {
  font-weight: 600;
  color: #a0aec0;
}

.targets h4 {
  font-size: 14px;
  color: #e2e8f0;
  margin-bottom: 8px;
}

.target-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}

.target-name {
  color: #cbd5e0;
  font-size: 14px;
}

.target-count {
  color: #f7fafc;
  font-weight: 600;
  font-size: 14px;
}

.search-field {
  margin-bottom: 16px;
}

.target-input {
  width: 100%;
  padding: 10px;
  background-color: #4a5568;
  border: 1px solid #718096;
  border-radius: 4px;
  color: #f7fafc;
  font-size: 14px;
}

.target-input::placeholder {
  color: #a0aec0;
}

.no-data,
.no-data-message {
  color: #a0aec0;
  font-style: italic;
  font-size: 14px;
  text-align: center;
  padding: 20px 0;
}

/* スマホ向け最適化 */
@media (max-width: 640px) {
  .px-4 {
    padding-left: 12px;
    padding-right: 12px;
  }

  .status-count {
    padding: 12px 8px;
  }

  .status-label {
    font-size: 12px;
  }

  .status-number {
    font-size: 20px;
  }
}
</style>
